import React, { useState } from 'react';
import { Trophy, X, RefreshCw } from 'lucide-react';

const WineRecommendation = ({ recommendation, type }) => {
  const [expanded, setExpanded] = useState(false);
  const { mainRecommendation, premiumOption, valueOption } = recommendation;

  const typeConfig = {
    main: {
      wine: mainRecommendation,
      title: "SOMMELIER'S SELECTION",
      icon: '⭐'
    },
    premium: {
      wine: premiumOption,
      title: 'CELLAR PRESTIGE',
      icon: '✨'
    },
    value: {
      wine: valueOption,
      title: 'HOUSE FAVORITE',
      icon: Trophy,
      showTrophy: true
    }
  };

  const config = typeConfig[type] || typeConfig.main;
  
  const parseWineName = (name) => {
    const parts = name.split(' - ');
    if (parts.length === 2) {
      return {
        name: parts[0].trim(),
        location: parts[1].trim().toUpperCase()
      };
    }
    
    const locationMatch = name.match(/(VQA .*|France|Italy|Spain|Argentina|Chile|California.*|Mendoza.*|Bordeaux.*|Burgundy.*)$/i);
    if (locationMatch) {
      const location = locationMatch[0].trim();
      const wineName = name.substring(0, name.lastIndexOf(location)).trim();
      return {
        name: wineName,
        location: location.toUpperCase()
      };
    }
    
    return { name, location: '' };
  };

  const { name: wineName, location } = parseWineName(config.wine.name);
  
  const parsePrice = (priceStr) => {
    if (!priceStr) return null;
    
    // Handle glass/bottle format: $XX/glass, $XX/bottle or $XX.X/glass, $XX.X/bottle
    const glassBottleMatch = priceStr.match(/\$?([\d.]+)\s*\/\s*glass\s*,?\s*\$?([\d.]+)\s*\/\s*bottle/i);
    if (glassBottleMatch) {
      return {
        glass: Math.round(parseFloat(glassBottleMatch[1])),
        bottle: Math.round(parseFloat(glassBottleMatch[2])),
        hasGlass: true
      };
    }
    
    // Handle bottle only: $XX/bottle or $XX.X/bottle
    const bottleOnlyMatch = priceStr.match(/\$?([\d.]+)\s*\/\s*bottle/i);
    if (bottleOnlyMatch) {
      return {
        bottle: Math.round(parseFloat(bottleOnlyMatch[1])),
        hasGlass: false
      };
    }
    
    // Handle simple price: $XX or $XX.X
    const simpleMatch = priceStr.match(/\$?([\d.]+)/);
    if (simpleMatch) {
      return {
        bottle: Math.round(parseFloat(simpleMatch[1])),
        hasGlass: false
      };
    }
    
    return null;
  };

  const pricing = parsePrice(config.wine.price);

  const handleClick = () => {
    setExpanded(!expanded);
  };

  return (
    <div 
      onClick={handleClick}
      className={`bg-white rounded-xl border overflow-hidden transition-all duration-300 cursor-pointer
        ${expanded 
          ? 'border-[#D4704A] shadow-lg scale-[1.02]' 
          : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
        }`}
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-2 flex justify-between items-start">
        <div className="flex items-center gap-1.5">
          {config.showTrophy ? (
            <Trophy className="h-3.5 w-3.5 text-[#D4704A]" />
          ) : (
            <span className="text-sm">{config.icon}</span>
          )}
          <span className="text-[10px] font-semibold tracking-widest uppercase text-[#D4704A]">
            {config.title}
          </span>
        </div>
        {expanded && (
          <button 
            onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Wine details */}
      <div className="px-5 pb-4">
        <div className="flex items-baseline gap-2 mb-0.5">
          <h3 className="text-lg font-medium text-gray-900" style={{fontFamily: 'Georgia, serif'}}>
            {wineName}
          </h3>
          {config.wine.varietal && (
            <span className="text-xs font-medium text-[#D4704A]">
              {config.wine.varietal}
            </span>
          )}
        </div>
        
        {location && !location.match(/^\$?\d+/) && (
          <p className="text-[10px] font-medium tracking-wider uppercase text-gray-400 mb-3">
            {location}
          </p>
        )}

        {pricing && (
          <div className="flex items-center gap-3 mb-3">
            {pricing.hasGlass && (
              <>
                <div>
                  <span className="text-lg font-bold text-gray-900">${pricing.glass}</span>
                  <span className="text-[9px] font-medium tracking-wider text-gray-400 ml-1">GLASS</span>
                </div>
                <div className="h-4 w-px bg-gray-200"></div>
              </>
            )}
            <div>
              <span className="text-lg font-bold text-gray-900">${pricing.bottle}</span>
              <span className="text-[9px] font-medium tracking-wider text-gray-400 ml-1">BOTTLE</span>
            </div>
          </div>
        )}

        {/* Description - full when expanded */}
        <p className="text-xs leading-relaxed text-gray-500">
          {expanded ? config.wine.reason : (config.wine.reason?.substring(0, 60) + (config.wine.reason?.length > 60 ? '...' : ''))}
        </p>
      </div>
    </div>
  );
};

const WineRecommendationSet = ({ recommendation, onRefresh }) => {
  return (
    <div className="space-y-3 w-full">
      <WineRecommendation recommendation={recommendation} type="main" />
      
      <div className="grid md:grid-cols-2 gap-3">
        <WineRecommendation recommendation={recommendation} type="premium" />
        <WineRecommendation recommendation={recommendation} type="value" />
      </div>

      {recommendation.pairingNote && (
        <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 hover:border-gray-200 transition-all duration-200">
          <div className="flex gap-2.5">
            <span className="text-base shrink-0">🍽️</span>
            <div>
              <p className="text-[10px] font-semibold tracking-widest uppercase text-[#D4704A] mb-0.5">PERFECT WITH</p>
              <p className="text-sm leading-relaxed text-gray-500">{recommendation.pairingNote}</p>
            </div>
          </div>
        </div>
      )}

      {onRefresh && (
        <div className="flex justify-center pt-1">
          <button
            onClick={onRefresh}
            data-testid="refresh-wines-btn"
            className="w-9 h-9 rounded-full bg-[#D4704A] hover:bg-[#B85A3A] active:scale-90 flex items-center justify-center transition-all duration-200 shadow-sm"
          >
            <RefreshCw className="w-4 h-4 text-white" />
          </button>
        </div>
      )}
    </div>
  );
};

export { WineRecommendation, WineRecommendationSet };
